/**
 * Client-side duress vault.
 *
 * When a user sets up a duress password we seed a SEPARATE local
 * vault containing plausible-looking contacts and innocuous chat
 * history. When the user logs in with the duress password, the app
 * unlocks this vault instead of the real one — so the coercer sees
 * a complete, normal-looking messenger with real-feeling data but
 * the genuine account stays cryptographically invisible.
 *
 * Threat model:
 *   - Coerced unlock (border, police, kidnapper, partner): they see
 *     the decoy, the real account is sealed.
 *   - Server compromise: the decoy vault lives ENTIRELY client-side
 *     (encrypted with a PBKDF2 derived from the duress password).
 *     A server dump reveals only a bcrypt hash of the duress
 *     password and a log of duress-unlock events — not the contents.
 *   - Forensic disk image of the device: the vault is AES-GCM
 *     encrypted at rest. Without the duress password, the blob is
 *     indistinguishable from noise.
 *
 * Known v1 gaps:
 *   - A coercer who forces the user to log in on a brand-new
 *     device can't be decoy'd by this module alone — that requires
 *     server-side decoy responses (deferred).
 *   - Scripted auto-replies are time-randomised but not
 *     contextually adaptive — an attacker who scripts "reply to
 *     every 3 minutes with the weather" will notice the decoy
 *     falls behind within a session.
 */

const VAULT_KEY = 'ghostcomm_duress_vault';
const VAULT_VERSION = 1;
const PBKDF2_ITERATIONS = 210000;

// Deterministic roster so the decoy population is stable across
// unlocks. A coercer can't spot "hey wait, my fake contacts changed
// between unlocks" if the same password always reveals the same data.
// Names are intentionally short, common, and uninteresting.
const DECOY_ROSTER = [
  { name: 'Alex',   tag: '#alex-morn'   },
  { name: 'Jordan', tag: '#jord-east'   },
  { name: 'Sam',    tag: '#sam-brick'   },
  { name: 'Rio',    tag: '#rio-north'   },
  { name: 'Casey',  tag: '#casey-bat'   },
];

// Scripted decoy conversations. Each entry is { contactIndex, text,
// fromUser, minutesAgo }. The seeder materialises these into real
// message rows with rotated timestamps. Designed to read like
// benign, mundane logistics so there's no juicy content for the
// coercer to demand explanations for.
const DECOY_TRANSCRIPT = [
  { c: 0, t: "hey can you grab milk on the way back?",          me: false, m: 2880 },
  { c: 0, t: "sure. anything else?",                             me: true,  m: 2875 },
  { c: 0, t: "coffee pods if they have the dark roast",          me: false, m: 2870 },
  { c: 0, t: "got it",                                           me: true,  m: 2865 },
  { c: 1, t: "running about 10 min late for the thing tomorrow", me: false, m: 1440 },
  { c: 1, t: "np",                                               me: true,  m: 1435 },
  { c: 1, t: "thanks",                                           me: false, m: 1430 },
  { c: 2, t: "did you ever figure out the dishwasher?",          me: false, m: 720  },
  { c: 2, t: "yeah, just the filter was clogged",                me: true,  m: 715  },
  { c: 3, t: "library card showed up in the mail btw",           me: false, m: 360  },
  { c: 3, t: "oh nice",                                          me: true,  m: 358  },
  { c: 4, t: "can you pick me up at 5?",                         me: false, m: 120  },
  { c: 4, t: "yeah on my way",                                   me: true,  m: 118  },
];

// Plausible auto-reply patterns for messages the coerced user sends
// in duress mode. Delayed 4–12 seconds so the cadence feels human.
// Rotates through these to avoid obviously-canned repetition.
const AUTO_REPLY_TEMPLATES = [
  "k",
  "sure",
  "got it",
  "np",
  "on my way",
  "sounds good",
  "haha ok",
  "yeah",
  "will do",
  "see you soon",
  "thanks",
  "👍",
];
const AUTO_REPLY_DELAY_MS_MIN = 4000;
const AUTO_REPLY_DELAY_MS_MAX = 12000;

// ─── Crypto helpers (Web Crypto only) ───────────────────────────────

async function deriveVaultKey(duressPassword, salt) {
  const material = await window.crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(duressPassword),
    { name: 'PBKDF2' }, false, ['deriveKey'],
  );
  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

const b64encode = (bytes) => {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
};
const b64decode = (str) => Uint8Array.from(atob(str), (c) => c.charCodeAt(0));

// ─── Decoy seeder ───────────────────────────────────────────────────

function seedDecoyData(userId) {
  // Generate stable synthetic IDs so the SAME vault always has the
  // same contact/conv ids across unlocks.
  const mkId = (prefix, i) => `decoy-${prefix}-${i.toString(36)}`;
  const now = Date.now();
  const contacts = DECOY_ROSTER.map((p, i) => ({
    id: mkId('u', i),
    username: p.name,
    email: null,
    contact_tag: p.tag,
    public_key: null,
    created_at: new Date(now - 30 * 86400_000).toISOString(),
    is_online: i % 2 === 0,
  }));
  const conversations = contacts.map((c, i) => ({
    id: mkId('c', i),
    type: 'direct',
    participants: [userId, c.id],
    encrypted_name: null,
    created_at: new Date(now - 30 * 86400_000).toISOString(),
    last_message_at: null,
  }));
  const messages = [];
  let msgCounter = 0;
  for (const row of DECOY_TRANSCRIPT) {
    const contact = contacts[row.c];
    const conv = conversations[row.c];
    const ts = new Date(now - row.m * 60_000).toISOString();
    messages.push({
      id: mkId('m', msgCounter++),
      conversation_id: conv.id,
      sender_id: row.me ? userId : contact.id,
      // Plaintext — the duress vault stores the decoy text directly
      // because there's no encryption benefit against the coercer
      // (they already got the vault open by definition).
      _plain: row.t,
      // But keep the shape identical to real messages so the UI
      // doesn't need duress-specific branches everywhere.
      encrypted_content: null,
      encrypted_keys: {},
      created_at: ts,
      expires_at: new Date(now + 86400_000).toISOString(),
      is_read: true,
      reactions: {},
      edited: false,
      deleted: false,
    });
    conv.last_message_at = ts;
  }
  return { contacts, conversations, messages };
}

// ─── Vault I/O ──────────────────────────────────────────────────────

/**
 * First-time seeding. Encrypts a fresh decoy vault against the duress
 * password and stores it in localStorage. Returns the raw decoy state
 * so the setup wizard can preview what the coercer would see.
 */
export async function seedDuressVault(duressPassword, userId) {
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveVaultKey(duressPassword, salt);
  const state = seedDecoyData(userId);
  const ct = new Uint8Array(
    await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(JSON.stringify(state)),
    ),
  );
  localStorage.setItem(
    VAULT_KEY,
    JSON.stringify({
      version: VAULT_VERSION,
      salt: b64encode(salt),
      iv: b64encode(iv),
      ct: b64encode(ct),
      // NOTE: we do NOT store the userId here — a forensic
      // inspection of the blob must not reveal the owner.
      created_at: new Date().toISOString(),
    }),
  );
  return state;
}

/**
 * Attempt to open the vault with a candidate password. Returns the
 * decoy state on success, null on any failure (wrong password,
 * missing vault, tampered blob). Caller treats null as "no decoy
 * vault exists for this password — continue with primary login
 * flow".
 */
export async function tryOpenDuressVault(candidatePassword) {
  const raw = localStorage.getItem(VAULT_KEY);
  if (!raw) return null;
  try {
    const env = JSON.parse(raw);
    if (env.version !== VAULT_VERSION) return null;
    const salt = b64decode(env.salt);
    const iv = b64decode(env.iv);
    const ct = b64decode(env.ct);
    const key = await deriveVaultKey(candidatePassword, salt);
    const pt = new Uint8Array(
      await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct),
    );
    return JSON.parse(new TextDecoder().decode(pt));
  } catch {
    return null;
  }
}

export function hasDuressVault() {
  return !!localStorage.getItem(VAULT_KEY);
}

export function deleteDuressVault() {
  localStorage.removeItem(VAULT_KEY);
}

// ─── Auto-reply scheduler ───────────────────────────────────────────

/**
 * Pick a plausible auto-reply template and schedule a delayed
 * callback. Caller supplies the `onReply` handler which should
 * inject a new inbound message into the duress state.
 *
 * Returns a cancel fn so a second message sent during the delay
 * window can cancel a pending reply (otherwise we'd stack replies
 * faster than a real human would send them).
 */
export function scheduleAutoReply(onReply) {
  const idx = Math.floor(Math.random() * AUTO_REPLY_TEMPLATES.length);
  const text = AUTO_REPLY_TEMPLATES[idx];
  const delay =
    AUTO_REPLY_DELAY_MS_MIN +
    Math.floor(Math.random() * (AUTO_REPLY_DELAY_MS_MAX - AUTO_REPLY_DELAY_MS_MIN));
  const t = setTimeout(() => onReply(text), delay);
  return () => clearTimeout(t);
}

export const __internal = { AUTO_REPLY_TEMPLATES, DECOY_ROSTER };
